import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { FC } from "react";

import {
  additionalAddressFields,
  userNamesFields,
} from "web/Layout/SubscriptionReceivers/SubscriptionReceiverDuplicateCard/DuplicateCardForm/DuplicateSelect/additionalFormFields";
import __ from "web/Layout/Translations";

import {
  DUPLICATE_DATA_TYPE,
  DUPLICATE_REASONS_ID,
} from "web/constants/benefits";

import {
  DuplicateCardFormActions,
  DuplicateCardPayload,
  IDuplicateCardFormState,
  IDuplicateFormItem,
  List,
} from "web/types/Subscription";

interface IDispatchTypes {
  type: DuplicateCardFormActions | string;
  payload?: DuplicateCardPayload;
}

type ListEle = List[];

interface IDuplicateSelect {
  formItem: IDuplicateFormItem;
  disptachFormFieldsState: ({ type, payload }: IDispatchTypes) => void;
  formFieldsState: IDuplicateCardFormState;
  textInputsHandler: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
    required: boolean
  ) => void;
  placeDuplicateDeliveredType: string;
  personalDataError: boolean;
  setPersonalDataError: React.Dispatch<React.SetStateAction<boolean>>;
}

const messages = {
  personalDataFieldError: "Wpisane dane są takie same jak obecne",
  personalDataNotChangedError: `Wybierając "Zmieniły się dane personalne" nie możesz zamówić 
  duplikatu karty na te same dane, które były dotychczas. Zmień powód zamówienia duplikatu lub wpisz poprawne dane.`,
};

const DuplicateSelect: FC<IDuplicateSelect> = ({
  formItem,
  disptachFormFieldsState,
  formFieldsState,
  textInputsHandler,
  placeDuplicateDeliveredType,
  personalDataError,
  setPersonalDataError,
}) => {
  const changeActive = (
    allOptions: ListEle,
    chosenOption: number,
    type: string
  ) => {
    if (
      type === "reason" &&
      allOptions[chosenOption].id !== DUPLICATE_REASONS_ID.change &&
      allOptions[chosenOption].id !== DUPLICATE_REASONS_ID.incorrect &&
      placeDuplicateDeliveredType !== DUPLICATE_DATA_TYPE.address
    ) {
      disptachFormFieldsState({
        type: DuplicateCardFormActions.CLEAR_NAME_LASTNAME,
      });
    }

    if (
      (type === "reason" &&
        allOptions[chosenOption].id === DUPLICATE_REASONS_ID.change) ||
      allOptions[chosenOption].id === DUPLICATE_REASONS_ID.incorrect ||
      placeDuplicateDeliveredType === DUPLICATE_DATA_TYPE.address
    ) {
      const firstNameValue = formFieldsState.newFirstName;
      const lastNameValue = formFieldsState.newLastName;
      disptachFormFieldsState({
        type: DuplicateCardFormActions.NAME_LASTNAME_REQUIRED,
        payload: {
          newFirstName:
            typeof firstNameValue !== "string" ? firstNameValue?.value : "",
          newLastName:
            typeof lastNameValue !== "string" ? lastNameValue?.value : "",
        },
      });
    }

    disptachFormFieldsState({
      type,
      payload: { ...allOptions[chosenOption], required: formItem.required },
    });
    setPersonalDataError(false);
  };

  const preparedFormItem = { ...formItem };

  if (
    formItem.inputType === "select" &&
    formItem.type === "place" &&
    placeDuplicateDeliveredType !== DUPLICATE_DATA_TYPE.own &&
    placeDuplicateDeliveredType !== DUPLICATE_DATA_TYPE.mp
  ) {
    preparedFormItem.list = formItem.list.map((ele) => {
      return { id: ele.id, value: ele.name };
    });
  }

  const ifShowAdditionalFormData =
    (preparedFormItem.type === "reason" &&
      placeDuplicateDeliveredType !== DUPLICATE_DATA_TYPE.address &&
      (formFieldsState?.reason?.id === DUPLICATE_REASONS_ID.change ||
        formFieldsState?.reason?.id === DUPLICATE_REASONS_ID.incorrect)) ||
    placeDuplicateDeliveredType === DUPLICATE_DATA_TYPE.address;

  const currSelectValue =
    formFieldsState[preparedFormItem.type as keyof IDuplicateCardFormState];
  const selectValue =
    typeof currSelectValue !== "string" ? currSelectValue?.value : "";

  const { reason } = formFieldsState;
  const reasonId = reason?.id ?? "";

  const isReasonChangeOrIncorrect =
    reasonId === DUPLICATE_REASONS_ID.change ||
    reasonId === DUPLICATE_REASONS_ID.incorrect;

  const handleChange = (event: SelectChangeEvent) => {
    const optionsArray = preparedFormItem.list;
    const { value } = event.target;

    const optionsIndex = optionsArray.findIndex((option) => {
      const translatedOptions = option.value ? __(option.value) : option.value;
      return translatedOptions === value;
    });

    changeActive(optionsArray, optionsIndex, preparedFormItem.type);
  };

  return preparedFormItem.inputType === "select" &&
    preparedFormItem.showWhen.includes(placeDuplicateDeliveredType) ? (
    <Box>
      <Box>
        <Box sx={{ minWidth: 120, my: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">
              {preparedFormItem.label}*
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={
                typeof formFieldsState[
                  preparedFormItem.type as keyof IDuplicateCardFormState
                ] === "object" && selectValue
                  ? __(selectValue)
                  : preparedFormItem.placeholder
              }
              renderValue={(selected) => {
                // Sprawdzamy, czy wybrana wartość to placeholder
                if (selected === preparedFormItem.placeholder) {
                  return (
                    <span style={{ fontWeight: 400, color: "#9e9e9e" }}>
                      {preparedFormItem.placeholder}
                    </span>
                  );
                }
                // Jeśli to nie jest placeholder, zwracamy normalnie
                return selected;
              }}
              label={preparedFormItem.label}
              onChange={handleChange}
            >
              {preparedFormItem.list?.map((option) => {
                const optionValue =
                  option && typeof option !== "string" ? option.value : "";
                const prepOptionValue =
                  typeof optionValue === "string"
                    ? __(optionValue)
                    : optionValue;

                return (
                  <MenuItem
                    key={option.id}
                    value={prepOptionValue}
                    sx={{ fontSize: "16px", fontWeight: 700 }}
                  >
                    {prepOptionValue}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {ifShowAdditionalFormData && (
        <>
          {isReasonChangeOrIncorrect && (
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 2 }}>
              {__("Wprowadź dane do nowej karty")}
            </Typography>
          )}
          {userNamesFields(isReasonChangeOrIncorrect)
            .sort((a, b) => a.position - b.position)
            .map((field) => {
              const currValue =
                formFieldsState[field.type as keyof IDuplicateCardFormState];
              return (
                <>
                  <Box>
                    <TextField
                      sx={{
                        width: "100%",
                        mb: 2,
                      }}
                      label={`${__(field.label)}*`}
                      data-t1={field.label}
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={
                        currValue && typeof currValue !== "string"
                          ? currValue.value
                          : ""
                      }
                      onChange={(e: any) => {
                        setPersonalDataError(false);
                        textInputsHandler(e, field.type, field.required);
                      }}
                    />
                    {personalDataError && (
                      <Typography variant="subtitle1">
                        {messages.personalDataFieldError}
                      </Typography>
                    )}
                  </Box>
                  {field?.textBelow !== "" &&
                    placeDuplicateDeliveredType !==
                      DUPLICATE_DATA_TYPE.address && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          bgcolor: "#FFF4E5",
                          p: 2,
                          mt: 1,
                          mb: 3,
                        }}
                      >
                        <WarningAmberIcon
                          color="warning"
                          sx={{
                            width: "16px",
                            marginRight: "12px",
                            position: "relative",
                            top: "-5px",
                          }}
                        />
                        <Typography variant="body2">
                          {__(field?.textBelow)}
                        </Typography>
                      </Box>
                    )}
                </>
              );
            })}
        </>
      )}
      {placeDuplicateDeliveredType === "address" &&
        additionalAddressFields
          .sort((a, b) => a.position - b.position)
          .map((field) => {
            const currValue =
              formFieldsState[field.type as keyof IDuplicateCardFormState];
            return (
              <Box>
                <TextField
                  sx={{
                    width: "100%",
                    mb: 2,
                  }}
                  label={`${__(field.label)}*`}
                  data-t1={field.label}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={
                    currValue && typeof currValue !== "string"
                      ? currValue.value
                      : ""
                  }
                  onChange={(e: any) =>
                    textInputsHandler(e, field.type, field.required)
                  }
                />
              </Box>
            );
          })}
    </Box>
  ) : null;
};

export default DuplicateSelect;
